import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "Aprendices";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const DetalleAprendiz = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [aprendiz, setAprendiz] = useState(null);
  const [loading, setLoading] = useState(false);

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloAprendices = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloAprendices = useMemo(() => getModuloAprendices(), []);

  const puedeLeer =
    moduloAprendices?.puedeLeer === true ||
    moduloAprendices?.leer === true ||
    moduloAprendices?.ver === true ||
    moduloAprendices?.listar === true ||
    moduloAprendices?.read === true ||
    moduloAprendices?.view === true;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const fetchAprendiz = useCallback(async () => {
    if (!puedeLeer) {
      setAprendiz(null);
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/aprendices/listid/${id}`,
        authConfig()
      );

      if (data?.ok) {
        setAprendiz(data.message);
      } else {
        setAprendiz(null);
      }
    } catch (error) {
      console.error("Error al obtener los datos:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "No se pudo cargar la información del aprendiz.",
        "error"
      );

      setAprendiz(null);
    } finally {
      setLoading(false);
    }
  }, [id, puedeLeer, manejarErrorSesion]);

  useEffect(() => {
    if (id) fetchAprendiz();
  }, [id, fetchAprendiz]);

  if (!puedeLeer) {
    return (
      <div className="container py-5 px-5 shadow-lg rounded-4 bg-light mt-5 mb-5">
        <div className="text-center text-secondary py-5">
          <div className="display-6 mb-3">
            <i className="fa-solid fa-lock"></i>
          </div>

          <h5 className="fw-semibold text-dark">Sin permiso de lectura</h5>

          <p className="mb-4">
            No tienes permisos para visualizar el detalle del aprendiz.
          </p>

          <button
            className="btn btn-outline-dark"
            type="button"
            onClick={() => navigate(-1)}
          >
            <i className="fa-solid fa-arrow-rotate-left me-2"></i>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5 px-5 shadow-lg rounded-4 bg-light mt-5 mb-5">
      <h2>Detalle del Aprendiz</h2>

      {loading ? (
        <div className="text-center text-secondary py-5">
          <div className="spinner-border" role="status" />
          <div className="mt-3">Cargando información del aprendiz...</div>
        </div>
      ) : aprendiz ? (
        <div>
          <div className="row">
            <div className="col-md-4">
              <p>
                <strong>Documento:</strong> {aprendiz.documentoAprendiz}
              </p>

              <p>
                <strong>Tipo de Documento:</strong>{" "}
                {aprendiz.tipodocumentoAprendiz}
              </p>

              <p>
                <strong>Nombre Completo:</strong>{" "}
                {`${aprendiz.nombre1Aprendiz || ""} ${
                  aprendiz.nombre2Aprendiz || ""
                } ${aprendiz.apellido1Aprendiz || ""} ${
                  aprendiz.apellido2Aprendiz || ""
                }`}
              </p>

              <p>
                <strong>Edad:</strong> {aprendiz.edadAprendiz}
              </p>

              <p>
                <strong>Sexo:</strong> {aprendiz.sexoAprendiz}
              </p>

              <p>
                <strong>Dirección:</strong> {aprendiz.direccionAprendiz}
              </p>

              <p>
                <strong>Teléfono 1:</strong> {aprendiz.telefono1Aprendiz}
              </p>
            </div>

            <div className="col-md-4 mt-5 text-center">
              {aprendiz.imgAprendiz ? (
                <img
                  src={aprendiz.imgAprendiz}
                  alt="Foto del Aprendiz"
                  className="img-fluid rounded-circle shadow mb-3"
                  style={{
                    width: "200px",
                    height: "200px",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <p className="text-muted">Sin imagen disponible</p>
              )}
            </div>

            <div className="col-md-4">
              <p>
                <strong>Teléfono 2:</strong>{" "}
                {aprendiz.telefono2Aprendiz || "N/A"}
              </p>

              <p>
                <strong>Fecha de Nacimiento:</strong>{" "}
                {aprendiz.fechanacimientoAprendiz
                  ? new Date(
                      aprendiz.fechanacimientoAprendiz
                    ).toLocaleDateString("es-CO")
                  : "N/A"}
              </p>

              <p>
                <strong>RH:</strong> {aprendiz.rhAprendiz}
              </p>

              <p>
                <strong>Email:</strong> {aprendiz.emailAprendiz}
              </p>

              <p>
                <strong>País:</strong> {aprendiz.paisAprendiz}
              </p>

              <p>
                <strong>Estado:</strong> {aprendiz.estadoAprendiz}
              </p>

              <p>
                <strong>Ciudad:</strong> {aprendiz.ciudadAprendiz}
              </p>
            </div>
          </div>

          <button
            className="btn btn-secondary mt-3"
            type="button"
            onClick={() => navigate(-1)}
          >
            Volver
          </button>
        </div>
      ) : (
        <div className="text-center text-secondary py-5">
          <div className="fw-semibold">No se encontró el aprendiz</div>

          <button
            className="btn btn-outline-dark mt-3"
            type="button"
            onClick={() => navigate(-1)}
          >
            Volver
          </button>
        </div>
      )}
    </div>
  );
};

export default DetalleAprendiz;